/**
 * useAuth — Reactive wrapper over authService
 * -------------------------------------------
 * Provides reactive session state and auth actions for use in Vue components.
 * authService manages Firebase Auth + the session cache.
 * accountApi handles parent + children records in MongoDB.
 */
import { ref, computed } from 'vue';
import * as authService from '@/services/authService';
import { authReady } from '@/services/authService';
import { useUserStore } from '@/stores/userStore';
import { useAnalytics } from '@/composables/useAnalytics';

// Module-level singleton so all components share the same reactive session ref.
const session = ref(authService.getSession());

// After a full-page reload (e.g. post-Stripe redirect), Firebase onAuthStateChanged
// fires asynchronously and updates _session, but the module-level ref above was
// already initialised to null. Sync it once authReady resolves.
authReady.then(() => {
  if (!session.value) {
    session.value = authService.getSession();
  }
});

export function useAuth() {
  const userStore = useUserStore();
  const { identify, reset, track } = useAnalytics();
  const isAuthenticated = computed(() => session.value !== null);
  const isOnboarded = computed(() => session.value?.onboardingComplete === true);

  /** Sync userStore after any login / register so effectivePlan is available. */
  function _postLoginSync() {
    userStore.initFromStorage();
    userStore.syncAccount(); // non-blocking — refreshes subscription + onboardingComplete
    // Link PostHog events to the logged-in user
    if (session.value?.uid) {
      identify(session.value.uid, {
        email: session.value.email,
        name: session.value.name,
        plan: session.value.plan,
      });
    }
  }

  async function register(name, email, password) {
    session.value = await authService.register(name, email, password);
    track('signed_up', { method: 'email' });
    _postLoginSync();
  }

  async function login(email, password) {
    session.value = await authService.login(email, password);
    track('signed_in', { method: 'email' });
    _postLoginSync();
  }

  async function loginWithSocial(provider) {
    session.value = await authService.loginWithSocial(provider);
    track('signed_in', { method: provider });
    _postLoginSync();
  }

  async function logout() {
    await authService.logout();
    reset(); // dissociate PostHog identity on logout
    session.value = null;
  }

  /** Re-hydrate from the auth session cache (call on app boot). */
  function restoreSession() {
    session.value = authService.getSession();
  }

  async function sendPasswordReset(email) {
    await authService.sendPasswordReset(email);
  }

  async function verifyResetCode(oobCode) {
    return await authService.verifyResetCode(oobCode);
  }

  async function confirmPasswordReset(oobCode, newPassword) {
    await authService.confirmPasswordResetCode(oobCode, newPassword);
  }

  return {
    session,
    isAuthenticated,
    isOnboarded,
    register,
    login,
    loginWithSocial,
    logout,
    restoreSession,
    sendPasswordReset,
    verifyResetCode,
    confirmPasswordReset,
  };
}
